/**
 * Answers Merlin found elsewhere (kanban 3eed0be5).
 *
 * The daily answer sweep rules whether information that arrived since an
 * ask, checklist step or wait was raised already answers it. A confident
 * answer closes the item on its own; anything less sits here as a
 * candidate for one click: Confirm (close it with Merlin's answer and
 * evidence) or Still open (leave it; Merlin waits out the recheck window).
 *
 * Wraps `/api/quality-copilot/found-answers*` on the shared axios instance.
 */
import api from './api';

const BASE = '/quality-copilot/found-answers';

export type FoundAnswerKind = 'ask' | 'case_task' | 'thread_wait';
export type FoundAnswerStatus = 'candidate' | 'retired' | 'open';
export type FoundAnswerVerdict = 'answered' | 'not_answered' | 'unsure';

/** One evidence row behind a found answer, as the sweep cited it. */
export interface FoundAnswerEvidence {
  /** `<source>:<ref>` — meeting:…, synced_mail:…, order:AU…, chat:… */
  id: string;
  when: string | null;
  /** The words that carry the answer, when the source had them. */
  quote: string | null;
  why_it_counts: string;
}

export interface FoundAnswerRead {
  id: string;
  item_kind: FoundAnswerKind;
  item_id: string;
  status: FoundAnswerStatus;
  thread_id: string | null;
  thread_no: string;
  investigation_id: string | null;
  investigation_no: string;
  /** The question as the item states it (first line is the title). */
  question: string;
  person_name: string;
  raised_at: string;
  verdict: FoundAnswerVerdict | null;
  confidence: number | null;
  answer_summary: string | null;
  evidence: FoundAnswerEvidence[];
  ruled_at: string | null;
  retired_at: string | null;
  /** merlin:answer_sweep when the confidence carried it, jess:<id> when confirmed. */
  retired_by: string | null;
  declined_at: string | null;
  /** Door path of the item (case, else thread, else the door). */
  link: string;
}

/** Candidates oldest first, then rows retired within `includeRetiredDays`. */
export async function listFoundAnswers(includeRetiredDays = 7): Promise<FoundAnswerRead[]> {
  const { data } = await api.get<FoundAnswerRead[]>(BASE, {
    params: { include_retired_days: includeRetiredDays },
  });
  return data;
}

/** Yes, that answers it: close the item with Merlin's answer. 409 if not a candidate. */
export async function confirmFoundAnswer(rowId: string): Promise<FoundAnswerRead> {
  const { data } = await api.post<FoundAnswerRead>(`${BASE}/${rowId}/confirm`);
  return data;
}

/** No, still open: leave the item; Merlin waits out the recheck window. */
export async function declineFoundAnswer(rowId: string, reason?: string): Promise<FoundAnswerRead> {
  const { data } = await api.post<FoundAnswerRead>(
    `${BASE}/${rowId}/decline`,
    reason ? { reason } : {},
  );
  return data;
}
